import React, { useEffect, useId, useRef, useState } from 'react';
import ActionPillMenu from '../ui/ActionPillMenu';
import { LineIcon } from '../notifications/NotificationKindIcon';
import { syncFolders } from '../../lib/emailSync';

function useActionTooltip() {
    const [tip, setTip] = useState(false);
    const ref = useRef(null);
    const touch = useRef(false);
    const tapped = useRef(false);
    const id = useId();
    const hide = () => { setTip(false); tapped.current = false; };
    useEffect(() => {
        if (!tip) return undefined;
        const outside = event => { if (!ref.current?.contains(event.target)) hide(); };
        const timer = setTimeout(hide, 3000);
        document.addEventListener('pointerdown', outside);
        window.addEventListener('scroll', hide, true);
        return () => { clearTimeout(timer); document.removeEventListener('pointerdown', outside); window.removeEventListener('scroll', hide, true); };
    }, [tip]);
    return { ref, tip, id, touch, tapped, show: () => setTip(true), hide,
        pointer: event => { touch.current = event.pointerType === 'touch'; },
        tap: () => { tapped.current = !tapped.current; setTip(tapped.current); } };
}
function IconAction({ label, icon, disabled, onAction, confirmTouch = false }) {
    const tooltip = useActionTooltip();
    const { ref, tip, id, touch, tapped, show, hide, pointer, tap } = tooltip;
    return <span ref={ref} className="ia-email-icon-action" onMouseEnter={show} onMouseLeave={hide}>
        <button type="button" className="ia-btn3d ia-comms-pill" aria-label={label} aria-describedby={tip ? id : undefined}
            disabled={disabled} onFocus={show} onBlur={hide} onPointerDown={pointer}
            onClick={() => {
                if (touch.current) {
                    const confirmed = tapped.current;
                    tap();
                    if (confirmTouch && !confirmed) return;
                } else show();
                onAction();
            }}><LineIcon name={icon} /></button>
        {tip && <span id={id} role="tooltip" className="ia-email-action-tooltip">{confirmTouch && touch.current ? `${label} — tap again to confirm` : label}</span>}
    </span>;
}
function FolderAction({ folders, busy, onMove }) {
    const { ref, tip, id, touch, show, hide, pointer, tap } = useActionTooltip();
    return <span ref={ref} className="ia-email-icon-action" onMouseEnter={show} onMouseLeave={hide} onFocusCapture={show} onBlurCapture={hide} onPointerDown={pointer} onClickCapture={() => { if (touch.current) tap(); }}>
        <ActionPillMenu menuLabel="Move to folder" triggerLabel="Move to folder" triggerTitle="Move to folder" disabled={busy}
            triggerProps={{ 'aria-describedby': tip ? id : undefined }} triggerClassName="ia-btn3d ia-comms-pill"
            options={folders.map(folder => ({ key: folder, label: folder, onSelect: () => { hide(); onMove(folder); } }))}>
            <LineIcon name="folder" />
        </ActionPillMenu>
        {tip && <span id={id} role="tooltip" className="ia-email-action-tooltip">Move to folder</span>}
    </span>;
}
export default function EmailMessageActions({ message, onAction, folders = syncFolders({}) }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    if (!message?.emailSyncId || !onAction) return null;
    const run = async (action, value) => {
        setBusy(true); setError('');
        try { await onAction(message, action, value); } catch (e) { setError(e.message || 'Mailbox action failed'); } finally { setBusy(false); }
    };
    return <div className="ia-email-message-actions" role="group" aria-label="Email actions">
        <IconAction label={message.emailIsRead ? 'Mark unread' : 'Mark read'} icon="email" disabled={busy} onAction={() => run('read', !message.emailIsRead)} />
        <IconAction label="Move to Trash" icon="trash" confirmTouch disabled={busy} onAction={() => run('delete')} />
        <FolderAction folders={folders} busy={busy} onMove={folder => run('move', folder)} />
        {error && <span role="alert">{error}</span>}
    </div>;
}
